import { useEffect, useState } from "react";
import { Save } from "lucide-react";
import { toast } from "sonner";
import { adminApi } from "../api";
import ImageUploader from "../components/ImageUploader";
import VideoUploader from "../components/VideoUploader";
import { invalidateSettings } from "../../lib/useSettings";

const FIELDS = [
  { key: "address", label: "Indirizzo sede" },
  { key: "phone", label: "Telefono" },
  { key: "email", label: "Email principale", type: "email" },
  { key: "press_email", label: "Email stampa", type: "email" },
  { key: "hours", label: "Orari apertura" },
  { key: "linkedin", label: "URL LinkedIn", type: "url" },
  { key: "instagram", label: "URL Instagram", type: "url" },
  { key: "copyright_name", label: "Ragione sociale (copyright)" },
  { key: "built_by", label: "Built by (firma developer)" },
  { key: "built_by_url", label: "Link 'Built by' (opzionale)", type: "url" },
  { key: "privacy_url", label: "URL Privacy policy" },
  { key: "cookie_url", label: "URL Cookie policy" },
  { key: "legal_url", label: "URL Note legali" },
];

const ANNOUNCEMENT_FIELDS = [
  { key: "announcement_text", label: "Testo annuncio", placeholder: "Es. The Cathedral Duomo: iscriviti alla preview" },
  { key: "announcement_url", label: "URL destinazione (opzionale)", type: "url" },
  { key: "announcement_color", label: "Colore sfondo (hex)", placeholder: "#C48F56" },
];

const HERO_FIELDS = [
  { key: "hero_home", label: "Home — hero (opzionale, sovrascrive default)" },
  { key: "hero_about", label: "Chi Siamo — hero" },
  { key: "hero_hotels", label: "Hotel — hero" },
  { key: "hero_news", label: "News — hero" },
  { key: "hero_contact", label: "Contatti — hero" },
];

const HERO_TEXT_GROUPS = [
  {
    label: "Chi Siamo",
    eyebrow: "hero_about_eyebrow",
    title: "hero_about_title",
    subtitle: "hero_about_subtitle",
  },
  {
    label: "Hotel",
    eyebrow: "hero_hotels_eyebrow",
    title: "hero_hotels_title",
    subtitle: "hero_hotels_subtitle",
  },
  {
    label: "News",
    eyebrow: "hero_news_eyebrow",
    title: "hero_news_title",
    subtitle: "hero_news_subtitle",
  },
  {
    label: "Contatti",
    eyebrow: "hero_contact_eyebrow",
    title: "hero_contact_title",
    subtitle: "hero_contact_subtitle",
  },
];

export default function AdminSettings() {
  const [form, setForm] = useState(null);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    adminApi.get("/admin/settings").then((r) => setForm(r.data));
  }, []);

  if (!form) {
    return (
      <div className="text-white/40">Caricamento impostazioni…</div>
    );
  }

  const set = (k) => (e) => setForm((s) => ({ ...s, [k]: e.target.value }));

  const save = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      const res = await adminApi.put("/admin/settings", form);
      setForm(res.data);
      invalidateSettings();
      toast.success("Impostazioni aggiornate", {
        description: "Le modifiche sono già visibili sul sito pubblico.",
      });
    } catch (err) {
      const detail = err.response?.data?.detail;
      let description = "Controlla i campi e riprova";
      if (Array.isArray(detail)) {
        description = detail
          .map((d) => {
            const field = Array.isArray(d.loc) ? d.loc[d.loc.length - 1] : d.loc;
            return `${field}: ${d.msg?.replace(/^Value error, ?/, "")}`;
          })
          .slice(0, 3)
          .join(" · ");
      } else if (typeof detail === "string") {
        description = detail;
      }
      toast.error("Salvataggio fallito", { description });
    } finally {
      setSaving(false);
    }
  };

  return (
    <div data-testid="admin-settings-page">
      <div className="flex items-start justify-between gap-6 mb-10">
        <div>
          <div className="font-mono text-[10px] uppercase tracking-[0.25em] text-[#C48F56]">
            CMS
          </div>
          <h1 className="mt-2 font-display font-light text-5xl tracking-tightest">
            Impostazioni sito
          </h1>
          <p className="mt-3 text-white/60 max-w-xl">
            Contatti del footer, link legali, firma developer. Ogni modifica è
            immediatamente visibile sul sito pubblico.
          </p>
        </div>
      </div>

      <form
        onSubmit={save}
        className="rounded-2xl border border-white/10 bg-[#0F0F14] p-8 md:p-10 space-y-6 max-w-3xl"
      >
        <Section title="Contatti">
          {FIELDS.slice(0, 5).map((f) => (
            <Field key={f.key} f={f} value={form[f.key] || ""} onChange={set(f.key)} />
          ))}
        </Section>

        <Section title="Social">
          {FIELDS.slice(5, 7).map((f) => (
            <Field key={f.key} f={f} value={form[f.key] || ""} onChange={set(f.key)} />
          ))}
        </Section>

        <Section title="Copyright & Footer">
          {FIELDS.slice(7, 10).map((f) => (
            <Field key={f.key} f={f} value={form[f.key] || ""} onChange={set(f.key)} />
          ))}
        </Section>

        <Section title="Link legali">
          <div className="grid md:grid-cols-3 gap-5">
            {FIELDS.slice(10).map((f) => (
              <Field key={f.key} f={f} value={form[f.key] || ""} onChange={set(f.key)} compact />
            ))}
          </div>
        </Section>

        <Section title="Immagini hero pagine">
          <p className="text-xs text-white/40 -mt-2 mb-2">
            Immagine di copertina delle pagine principali. Puoi incollare un URL
            esterno o caricare un file. Lascia vuoto per usare il default.
          </p>
          <div className="space-y-6">
            {HERO_FIELDS.map((f) => (
              <div key={f.key}>
                <label className="block font-mono text-[10px] uppercase tracking-[0.25em] text-white/40 mb-2">
                  {f.label}
                </label>
                <ImageUploader
                  value={form[f.key] || ""}
                  onChange={(v) => setForm((s) => ({ ...s, [f.key]: v }))}
                  testId={`settings-${f.key}`}
                />
              </div>
            ))}
          </div>
        </Section>

        <Section title="Testi hero pagine">
          <p className="text-xs text-white/40 -mt-2 mb-4">
            Occhiello, titolo e sottotitolo di ogni pagina. Nel titolo usa{" "}
            <code className="text-[#C48F56]">*testo*</code> per mettere una
            parola in corsivo, e vai a capo normalmente (invio) per forzare un
            nuovo rigo.
          </p>
          <div className="space-y-8">
            {HERO_TEXT_GROUPS.map((g) => (
              <div
                key={g.label}
                className="border border-white/10 rounded-xl p-5 space-y-4 bg-black/30"
              >
                <div className="font-display text-xl text-[#C48F56]">
                  {g.label}
                </div>
                <Field
                  f={{ key: g.eyebrow, label: "Occhiello (uppercase piccolo)" }}
                  value={form[g.eyebrow] || ""}
                  onChange={set(g.eyebrow)}
                />
                <div>
                  <label className="block font-mono text-[10px] uppercase tracking-[0.25em] text-white/40 mb-2">
                    Titolo (supporta *corsivo* e newline)
                  </label>
                  <textarea
                    value={form[g.title] || ""}
                    onChange={set(g.title)}
                    rows={2}
                    data-testid={`settings-${g.title}`}
                    className="w-full bg-transparent border border-white/10 rounded-md px-3 py-2 text-sm focus:border-[#C48F56] outline-none resize-y font-display"
                  />
                </div>
                <div>
                  <label className="block font-mono text-[10px] uppercase tracking-[0.25em] text-white/40 mb-2">
                    Sottotitolo
                  </label>
                  <textarea
                    value={form[g.subtitle] || ""}
                    onChange={set(g.subtitle)}
                    rows={3}
                    data-testid={`settings-${g.subtitle}`}
                    className="w-full bg-transparent border border-white/10 rounded-md px-3 py-2 text-sm focus:border-[#C48F56] outline-none resize-y"
                  />
                </div>
              </div>
            ))}
          </div>
        </Section>

        <Section title="Booking Engine (navbar)">
          <p className="text-xs text-white/40 -mt-2 mb-2">
            Aggiunge un pulsante nella barra di navigazione accanto ad{" "}
            <em>Area Investitori</em>. Utile per collegare il booking engine di
            catena. Apre in una nuova scheda.
          </p>
          <label className="flex items-center gap-3 cursor-pointer">
            <input
              type="checkbox"
              checked={!!form.booking_engine_enabled}
              onChange={(e) =>
                setForm((s) => ({ ...s, booking_engine_enabled: e.target.checked }))
              }
              data-testid="settings-booking_engine_enabled"
              className="h-4 w-4 accent-[#C48F56]"
            />
            <span className="text-sm">
              Mostra pulsante <strong>Booking Engine</strong> nella navbar
            </span>
          </label>
          {form.booking_engine_enabled && (
            <div className="space-y-5 border-l-2 border-[#C48F56]/30 pl-5">
              <Field
                f={{
                  key: "booking_engine_label",
                  label: "Testo del pulsante",
                  placeholder: "Prenota",
                }}
                value={form.booking_engine_label || ""}
                onChange={set("booking_engine_label")}
              />
              <Field
                f={{
                  key: "booking_engine_url",
                  label: "URL destinazione (https://…)",
                  type: "url",
                  placeholder: "https://book.investhospitality.it",
                }}
                value={form.booking_engine_url || ""}
                onChange={set("booking_engine_url")}
              />
            </div>
          )}
        </Section>

        <Section title="Home — Video di sfondo (Hero)">
          <p className="text-xs text-white/40 -mt-2 mb-2">
            Video cinematico opzionale al posto dell'immagine hero della
            home. <strong className="text-white/70">Regole</strong>: max ~40s,
            MP4 H.264 + AAC (o WebM VP9) idealmente sotto i 6 MB, muto,
            loop, 1920×1080. Puoi <strong className="text-white/70">caricare il file direttamente</strong> (self-hosting, max 100 MB) oppure incollare un URL esterno (CDN, Cloudflare Stream, Vimeo). Se un utente ha risparmio dati, connessione
            lenta o preferenza "riduci animazioni", viene mostrato solo il
            poster.
          </p>
          <label className="flex items-center gap-3 cursor-pointer">
            <input
              type="checkbox"
              checked={!!form.hero_video_enabled}
              onChange={(e) =>
                setForm((s) => ({ ...s, hero_video_enabled: e.target.checked }))
              }
              data-testid="settings-hero_video_enabled"
              className="h-4 w-4 accent-[#C48F56]"
            />
            <span className="text-sm">
              Attiva <strong>video di sfondo</strong> sulla hero home
            </span>
          </label>
          {form.hero_video_enabled && (
            <div className="space-y-5 border-l-2 border-[#C48F56]/30 pl-5">
              <div>
                <label className="block font-mono text-[10px] uppercase tracking-[0.25em] text-white/40 mb-2">
                  Video MP4 (H.264/AAC · sorgente primaria)
                </label>
                <VideoUploader
                  value={form.hero_video_url_mp4 || ""}
                  onChange={(v) => setForm((s) => ({ ...s, hero_video_url_mp4: v }))}
                  testId="hero-video-mp4-uploader"
                />
              </div>
              <div>
                <label className="block font-mono text-[10px] uppercase tracking-[0.25em] text-white/40 mb-2">
                  Video WebM (VP9/AV1 · opzionale, più leggero)
                </label>
                <VideoUploader
                  value={form.hero_video_url_webm || ""}
                  onChange={(v) => setForm((s) => ({ ...s, hero_video_url_webm: v }))}
                  testId="hero-video-webm-uploader"
                />
              </div>
              <Field
                f={{
                  key: "hero_video_poster",
                  label: "Poster (frame iniziale · fallback + fast paint)",
                  placeholder: "/brand/… oppure https://…/poster.jpg",
                }}
                value={form.hero_video_poster || ""}
                onChange={set("hero_video_poster")}
              />
              <div>
                <label className="block font-mono text-[10px] uppercase tracking-[0.25em] text-white/40 mb-2">
                  Object-fit
                </label>
                <select
                  value={form.hero_video_object_fit || "cover"}
                  onChange={(e) =>
                    setForm((s) => ({ ...s, hero_video_object_fit: e.target.value }))
                  }
                  data-testid="settings-hero_video_object_fit"
                  className="w-full bg-transparent border-b border-white/15 focus:border-[#C48F56] outline-none py-2 text-sm"
                >
                  <option value="cover">Cover (riempi, ritaglia)</option>
                  <option value="contain">Contain (mostra tutto, letterbox)</option>
                </select>
              </div>
              <label className="flex items-center gap-3 cursor-pointer">
                <input
                  type="checkbox"
                  checked={!!form.hero_video_play_on_mobile}
                  onChange={(e) =>
                    setForm((s) => ({
                      ...s,
                      hero_video_play_on_mobile: e.target.checked,
                    }))
                  }
                  data-testid="settings-hero_video_play_on_mobile"
                  className="h-4 w-4 accent-[#C48F56]"
                />
                <span className="text-sm">
                  Riproduci anche su mobile (default: solo desktop, mobile
                  vede il poster per non consumare dati mobili)
                </span>
              </label>
            </div>
          )}
        </Section>

        <Section title="Home — Comportamento sezioni">
          <div className="grid md:grid-cols-2 gap-5">
            <div>
              <label className="block font-mono text-[10px] uppercase tracking-[0.25em] text-white/40 mb-2">
                Velocità striscia loghi (secondi)
              </label>
              <input
                type="number"
                min="10"
                max="200"
                value={form.marquee_speed_seconds ?? 40}
                onChange={(e) =>
                  setForm((s) => ({
                    ...s,
                    marquee_speed_seconds: parseInt(e.target.value, 10) || 40,
                  }))
                }
                data-testid="settings-marquee_speed_seconds"
                className="w-full bg-transparent border-b border-white/15 focus:border-[#C48F56] outline-none py-2 text-sm"
              />
              <p className="mt-1 text-[10px] text-white/40">
                Durata di un ciclo completo. Più alto = più lento. Default: 40s.
              </p>
            </div>
            <label className="flex items-start gap-3 cursor-pointer self-start pt-7">
              <input
                type="checkbox"
                checked={!!form.home_openings_show_all_cta}
                onChange={(e) =>
                  setForm((s) => ({
                    ...s,
                    home_openings_show_all_cta: e.target.checked,
                  }))
                }
                data-testid="settings-home_openings_show_all_cta"
                className="mt-1 h-4 w-4 accent-[#C48F56] shrink-0"
              />
              <span className="text-sm leading-relaxed">
                Mostra bottone <strong>&laquo;Tutte le nuove aperture&raquo;</strong> in fondo
                alla sezione home
                <span className="block text-[10px] text-white/40 mt-1">
                  Disattivato di default: la pagina <code>/nuove-aperture</code>{" "}
                  mostra le stesse aperture della home.
                </span>
              </span>
            </label>
          </div>
        </Section>

        <Section title="KPI Home (Hero & Intro)">
          <p className="text-xs text-white/40 -mt-2 mb-2">
            I valori sono calcolati automaticamente dal database (hotel pubblicati
            e somma camere). Compila i campi sotto solo per <strong>sovrascrivere</strong>{" "}
            il valore automatico. Lascia vuoto per ripristinare il conteggio
            automatico.
          </p>
          <div className="grid md:grid-cols-2 gap-5">
            <KpiField
              label="Hotel operativi"
              auto={form.kpi_hotels_override === null ? "auto" : null}
              value={form.kpi_hotels_override}
              onChange={(v) =>
                setForm((s) => ({
                  ...s,
                  kpi_hotels_override: v === "" ? null : parseInt(v, 10),
                }))
              }
              testId="kpi_hotels_override"
            />
            <KpiField
              label="Camere totali"
              value={form.kpi_rooms_override}
              onChange={(v) =>
                setForm((s) => ({
                  ...s,
                  kpi_rooms_override: v === "" ? null : parseInt(v, 10),
                }))
              }
              testId="kpi_rooms_override"
            />
            <KpiField
              label="Hotel in pipeline"
              value={form.kpi_pipeline_hotels_override}
              onChange={(v) =>
                setForm((s) => ({
                  ...s,
                  kpi_pipeline_hotels_override:
                    v === "" ? null : parseInt(v, 10),
                }))
              }
              testId="kpi_pipeline_hotels_override"
            />
            <KpiField
              label="Camere in costruzione"
              value={form.kpi_pipeline_rooms_override}
              onChange={(v) =>
                setForm((s) => ({
                  ...s,
                  kpi_pipeline_rooms_override:
                    v === "" ? null : parseInt(v, 10),
                }))
              }
              testId="kpi_pipeline_rooms_override"
            />
          </div>
        </Section>

        <Section title="Banner annuncio">
          <label className="flex items-center gap-3 cursor-pointer">
            <input
              type="checkbox"
              checked={!!form.announcement_enabled}
              onChange={(e) =>
                setForm((s) => ({ ...s, announcement_enabled: e.target.checked }))
              }
              data-testid="settings-announcement_enabled"
              className="h-4 w-4 accent-[#C48F56]"
            />
            <span className="text-sm">
              Mostra striscia d&apos;annuncio in cima al sito
            </span>
          </label>
          {form.announcement_enabled && (
            <div className="space-y-5 border-l-2 border-[#C48F56]/30 pl-5">
              {ANNOUNCEMENT_FIELDS.map((f) => (
                <Field
                  key={f.key}
                  f={f}
                  value={form[f.key] || ""}
                  onChange={set(f.key)}
                />
              ))}
              {form.announcement_text && (
                <div
                  className="rounded-lg p-3 text-black text-sm font-medium text-center"
                  style={{
                    backgroundColor: form.announcement_color || "#C48F56",
                  }}
                >
                  Anteprima: {form.announcement_text}
                </div>
              )}
            </div>
          )}
        </Section>

        <div className="flex justify-end pt-4">
          <button
            type="submit"
            disabled={saving}
            data-testid="admin-settings-save"
            className="inline-flex items-center gap-2 rounded-full bg-white text-black px-6 py-2.5 text-sm font-medium hover:bg-[#C48F56] disabled:opacity-50"
          >
            {saving ? "Salvataggio…" : "Salva impostazioni"}
            <Save size={14} />
          </button>
        </div>
      </form>
    </div>
  );
}

function Section({ title, children }) {
  return (
    <div>
      <p className="font-mono text-[10px] uppercase tracking-[0.25em] text-[#C48F56] mb-4">
        {title}
      </p>
      <div className="space-y-5">{children}</div>
    </div>
  );
}

function Field({ f, value, onChange, compact }) {
  return (
    <div>
      <label className={`block font-mono text-[10px] uppercase tracking-[0.25em] text-white/40 mb-2 ${compact ? "" : ""}`}>
        {f.label}
      </label>
      <input
        type={f.type || "text"}
        value={value}
        onChange={onChange}
        data-testid={`settings-${f.key}`}
        className="w-full bg-transparent border-b border-white/15 focus:border-[#C48F56] outline-none py-2 text-sm"
      />
    </div>
  );
}

function KpiField({ label, value, onChange, testId }) {
  const isAuto = value === null || value === undefined || value === "";
  return (
    <div>
      <label className="flex items-center justify-between font-mono text-[10px] uppercase tracking-[0.25em] text-white/40 mb-2">
        <span>{label}</span>
        <span
          className={`px-2 py-0.5 rounded-full text-[9px] tracking-[0.15em] ${
            isAuto
              ? "bg-emerald-400/10 text-emerald-400"
              : "bg-[#C48F56]/15 text-[#C48F56]"
          }`}
        >
          {isAuto ? "Auto" : "Manuale"}
        </span>
      </label>
      <input
        type="number"
        min="0"
        value={value ?? ""}
        onChange={(e) => onChange(e.target.value)}
        data-testid={`settings-${testId}`}
        placeholder="Auto (calcolato dal database)"
        className="w-full bg-transparent border-b border-white/15 focus:border-[#C48F56] outline-none py-2 text-sm"
      />
    </div>
  );
}

